import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import Footer from "../../components/layout/Footer";
import HeaderNavigation from "../../components/layout/HeaderNavigation";
import { scrollToTop } from "../../utils/scrollToTop";

const TermsAndConditions = () => {
  useEffect(() => {
    scrollToTop();
  }, []);
  return (
    <div className="abstract-bg-1">
      <Helmet>
        <title>Utiva | Terms And Conditions</title>
      </Helmet>

      <header className="relative pb-8 md:pb-28 header-with-abstract-1 text-center">
        <HeaderNavigation />
        <div className="pt-20 container mx-auto px-4">
          <p className="capitalize tracking-widest text-primary pb-4 text-base">
            Utiva Platform
          </p>

          <h2 className="text-3xl md:text-5xl text-default font-mono font-semibold pb-4">
            Terms of Service
          </h2>
        </div>
      </header>

      <section className="pt-6 pb-10 w-full lg:w-8/12 mx-auto mb-10">
        <div className="container mx-auto px-4 leading-8 text-gray text-base">
          <div className="mb-8">
            <p>
              Last updated: 09, April 2024
              <br /> <br />
              Please read these Terms of Service ("Terms" or "Terms of Service")
              carefully before using the{" "}
              <a href="/" className="text-primary">
                www.utiva.co
              </a>{" "}
              website (the “Platform” or “Utiva Platform”) operated by Utiva,
              Inc. and its subsidiaries ("Utiva", "us", "we", or "our"). For
              purposes of these Terms, “you” and “your” means you as a user of
              the Services. We may update these Terms by posting an updated
              version to our website (“Site”).
              <br /> <br />
              Your access to and use of the services and features we make
              available to you on the Platform (the “Services”) is conditioned
              upon your acceptance of and compliance with these Terms. These
              Terms apply to all visitors, users and others who wish to access
              or use the Services. By accessing or using the Services, you agree
              to be bound by these Terms and to receive all communications and
              notices from us electronically. If you disagree with any part of
              the Terms, then you do not have our permission to access the
              Service. If you have questions about these Terms, contact our
              Customer Support team by submitting a request here before
              accessing the Services.
              <br /> <br />
              If you are accessing or using the Services on behalf of a business
              or other legal entity (“Business”), (i) you represent that you are
              authorized to enter into, and bind the Business to these Terms and
              register for the Services; and (ii) the Business is legally and
              financially responsible for your access to and use of the
              Services, as well as for the use of the Services by others
              affiliated with you, including any employees, agents or
              contractors.
              <br /> <br />
              SECTION 24 ALSO INCLUDES YOUR WAIVER TO CONSOLIDATED OR CLASS
              ACTIONS AND YOUR AGREEMENT TO RESOLVE DISPUTES THROUGH BINDING
              ARBITRATION. PLEASE READ IT CAREFULLY AND ONLY CONSENT TO THESE
              TERMS IF YOU UNDERSTAND AND AGREE TO THIS PROVISION.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Overview
            </h3>
            <p>
              Utiva enables businesses to seamlessly manage relationships with
              local and international independent contractors. You may register
              to the Platform as either a “Contractor” or “Client”. Depending on
              the designation of your account, different Services may be
              available to you. Certain Services may also only be available
              subject to the payment of such fees detailed on the Platform.
            </p>
            <p>
              <strong>Client and Contractor Management Services</strong>
              <br />
              Clients use Utiva to seamlessly onboard Contractors to their
              accounts payable systems, conduct vendor due diligence, guide them
              on compliance as applicable, execute agreements and produce
              statements of work, and simplify the process of making payments to
              Contractors (“Client Management Services”).
              <br /> <br />
              Contractors use Utiva to easily connect with their clients,
              memorialize contract terms, store payment and bank account
              preferences, and unify payment flows through a single platform
              (“Contractor Management Services”).
            </p>
            <p>
              <strong>How it works:</strong>
              <br />
              <ul className="list-inside list-disc">
                <li>Registration is free.</li>
                <li>
                  Utiva collects compliance information from Contractors on
                  behalf of Clients.
                </li>
                <li>
                  Clients pay a monthly fee, fixed fee, or per active contract
                  fee and a percentage fee of the volume paid out, as further
                  set forth on the Utiva Platform.
                </li>
                <li>
                  Utiva's Contract generation tool allows Users to set
                  memorialised timelines, deliverables, fees, and acceptance
                  criteria.
                </li>
                <li>
                  Clients and Contractors agree that the acceptance criteria
                  have been met.
                </li>
                <li>
                  Clients make payments to Contractors through the Utiva
                  Platform using supported payment methods.
                </li>
                <li>Utiva makes payouts to Contractors.</li>
                <li>
                  Contractors receive owed funds after every payout until the
                  contract is terminated by either Client or Contractor.
                </li>
                <li>
                  In the event that the contract is a milestone contract, the
                  contract will continue until it is terminated.
                </li>
              </ul>
            </p>
            <p>
              <strong>Consultant Services</strong>
              <br />
              Clients may also use Utiva to receive the services of specialized
              consultants engaged by Utiva, Utiva EOR Partners and/or other
              third-party partners of Utiva (“Consultants”) for the provision to
              Clients of certain human resource and other related services
              (“Consultant Services”). The receipt of any Consultant Services is
              subject to the execution of a separate agreement between the
              Client and Utiva or the applicable Utiva EOR Partner, as set forth
              on the Utiva Platform (“Consultant Services Agreement”). In the
              event of a conflict between the Consultant Services Agreement and
              these Terms, the provisions of the Consultant Services Agreement
              shall prevail.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Definitions
            </h3>
            <p>
              Capitalized terms in these Terms of Service are defined as
              follows:
              <br />
              <br />
              “Account” is an account to access and use the Platform in
              accordance with the terms hereof.
              <br />
              <br />
              “Administrator” are Users with authority to, on behalf of a
              Business, apply for an Account, access the Services, manage an
              Account, and otherwise act on behalf of the Business.
              <br />
              <br />
              “Clients” are Users who use the Services to onboard Contractors,
              perform due diligence, generate and execute Contracts, make
              payments to Contractors for their services and/or in connection
              with the receipt of any Consultant Services.
              <br />
              <br />
              “Contracts” are the legally binding documents executed by Clients
              and Contractors using the Platform’s Contract generation tool that
              describe the services to be provided by the Contractor, payment
              terms, and other information regarding the legal relationship
              between a Client and a Contractor.
              <br />
              <br />
              “Contractors” are Users who offer and perform services to Clients
              as described in Contracts and who receive payments from Clients
              through the Platform.
              <br />
              <br />
              “Utiva EOR Partners” are third parties engaging with Consultants
              as an employer of record in connection with the provision of
              Consultant Services.
              <br />
              <br />
              “Disputes” are disagreements between a Client and a Contractor
              regarding performance of, or payment for the services described in
              a Contract.
              <br />
              <br />
              “Payment Service Provider(s)” are third-party financial service
              providers with respect to payment of funds by Users hereunder,
              including, payments from Clients and settlement of funds to
              Contractors as part of the Management Services.
              <br />
              <br />
              “Funds” are the funds payable to Contractors for the services
              described in a Contract.
              <br />
              <br />
              “Management Services” are any Client Management Services or
              Contractor Management Services.
              <br />
              <br />
              “Users” are Clients, Contractors, Administrators, and any other
              person authorized to access an Account.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Eligibility
            </h3>
            <p>
              We make the Platform and the Services available to Users 18 years
              of age or older. If you are aged between 16 and 17, you confirm
              that you have the permission and consent of a parent or legal
              guardian prior to utilizing the Platform and the Services. In
              cases where you are under 16, or if such usage is prohibited by
              law, you may not create an Account or use the Services. You must
              be a human to open an Account. Accounts registered by “bots” or
              other automated methods are not permitted. You may not maintain
              more than one Account at a time. By applying for an Account and
              using the Services, you represent and warrant that you are of
              legal age to form a binding contract and meet all of the foregoing
              eligibility requirements. If you do not meet these requirements,
              you may not access or use the Services or apply for an Account.
              Utiva may refuse to provide open an Account for any individual or
              entity at its sole discretion.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Utiva Accounts
            </h3>
            <p>
              <strong>Applying for an Account</strong>
              <br />
              In order to access and use the Services, you will be required to
              create an account. To apply for an Account, you must provide your
              full legal name, a valid email address, your social security
              number, EIN, TIN, legal address and any other required information
              to complete the sign-up process, as requested by Utiva. If you are
              applying for an Account on behalf of a Business, you may also be
              required to provide a business address, business ownership
              details, the nature of the business and other business information
              that we may request. We will use this information to permit Utiva
              and its financial partners to conduct due diligence on you prior
              to opening an Account, and throughout the course of our business
              relationship with you. Our use of your information is subject to
              the terms of our Privacy Policy (“Privacy Policy”). If you do not
              agree to the terms of our Privacy Policy, do not apply for an
              Account. If you are applying for an Account on behalf of a
              Business, you must specify at least one Administrator. You are
              liable for any actions of your Administrator, and for any other
              person with access to your credentials or your Account. If you are
              an Administrator, you represent and warrant that you have the
              authority to apply for and to manage your Business’ Account.
            </p>

            <p>
              <strong>Account Security</strong>
              <br />
              You are fully and solely responsible for maintaining the privacy
              and security of your computer system, mobile device and all
              activity on your Account, even if such activities were not
              committed by you. You will promptly disable access to the Services
              if you believe your Account has been compromised or stolen, and
              you will immediately notify us if you believe your Account
              credentials have been compromised or stolen, and in the event of
              any unauthorized access to or use of your Account. Utiva will not
              be liable for any losses or damage arising from unauthorized use
              of your account or password, and you agree to indemnify and hold
              Utiva harmless for any unauthorized, improper or illegal use of
              your account and any charges and taxes incurred, unless you have
              notified us via e-mail at info@utiva.co that your account has been
              compromised and have requested that we block access to it, which
              we will do as soon as reasonably practicable. We will attempt to
              prevent unauthorized transactions or other activity using your
              Account, and we will assist you in the event your Account is
              compromised, but we do not police for and cannot guarantee that we
              will learn of or prevent, any inappropriate use of the Services
              and you are solely responsible for any financial or other loss
              that results from unauthorized access to your Account. We may
              suspend access to your Account if we suspect your Account has been
              compromised.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Use of Services
            </h3>
            <p>
              Subject to these Terms and the terms of any Consultant Services
              Agreement, as applicable, Utiva allows you to access and use the
              Platform and/or Services on a non-exclusive basis solely for your
              or the Business’, as applicable, internal business purposes. Utiva
              may, at its sole discretion and at any time, modify or discontinue
              providing the Services or any part thereof without notice.
              <br />
              <br />
              Use of and access to the Platform and/or Services is void where
              prohibited by law. By using the Services, you (where relevant on
              behalf of the applicable Business) represent and warrant that (a)
              any and all registration information you submit is truthful and
              accurate; (b) you will maintain the accuracy of such information;
              (c) your use of the Services does not violate any applicable law,
              regulation, or obligation you may have to a third party; (d) will
              not add any Personal Data (as defined in the DPA) of any
              third-party (“Third-Party“) to the Services, without the prior
              consent of the applicable Third-Party, and shall ensure that a
              record of such consents is maintained, all as required under
              applicable law; and (e) you shall comply with applicable laws,
              regulations, guidelines, these Terms and any Consultant Services
              Agreement throughout your use of the Platform and/or Services.
            </p>
            <p>
              <strong>Third Party Providers</strong>
              The Client acknowledges that the Services may integrate or
              interoperate with products and services of third parties
              (“Third-Party Products”), or import or export data and other
              content to or from Third-Party Products (collectively,
              “Integrations”).
              <br />
              <br />
              Any use of any Third-Party Products is solely at Client’s own
              risk. Utiva is not responsible for Third Party Products, any use
              by Client of Third-Party Products, and any exchange of data
              between Client and any Third-Party Product is solely between
              Client and the applicable provider.
              <br />
              <br />
              Utiva makes no representation, warranty or commitment and shall
              have no liability or obligation whatsoever in relation to the
              content or use of, or correspondence with, any such Third-Party
              Products, or any transactions completed, and any contract entered
              into by the Client, with any provider of such Third-Party
              Products. Any contract entered into and any transaction completed
              via any Third-Party Product is between the Client and the relevant
              third party, and not Utiva. The use of any Third-Party Products is
              subject to the applicable third party's terms and conditions and
              privacy policy. Utiva does not endorse or approve any Third-Party
              Products nor the content of any of the Third-Party Product made
              available via the Services.
              <br />
              <br />
              If Client opts to use any Integrations, Client agrees to: (i)
              assume sole responsibility for and all risk arising from Client’s
              use of Integrations and the content, functionality, or
              availability of any Third-Party Products, including waiving and
              releasing Utiva from any claims directly or indirectly related
              thereto; and (ii) without limitation, defend, indemnify, and hold
              harmless Utiva from any third party claims directly or indirectly
              arising from or related to Client’s use of any Integrations.
              <br />
              <br />
              Utiva PROVIDES INTEGRATIONS “AS IS'' WITHOUT WARRANTY OF ANY KIND
              AND ONLY AS A CONVENIENCE.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Content
            </h3>
            <p>
              Certain types of content may be made available through the
              Services. “Content” as used in these Terms means, collectively,
              all content on or made available through the Services, including
              any documents, images, photos, pictures, videos, data, audio or
              text, and any modifications or derivatives of the foregoing. Utiva
              allows you to upload certain content including but not limited to
              documents, text and other data on or through the Services,
              referred to herein as “User Content”.
              <br />
              <br />
              EXCEPT AS EXPRESSLY SET FORTH IN ANY CONSULTANT SERVICES
              AGREEMENT, Utiva DOES NOT ENDORSE ANY CONTENT (INCLUDING WITHOUT
              LIMITATION ANY USER CONTENT) OR ANY OPINION, RECOMMENDATION, OR
              ADVICE EXPRESSED IN ANY CONTENT AND EXPRESSLY DISCLAIMS ANY AND
              ALL LIABILITY IN CONNECTION WITH THE CONTENT AND/OR USER CONTENT.
              WE DISCLAIM ALL LIABILITY, REGARDLESS OF THE FORM OF ACTION, FOR
              THE ACTS OR OMISSIONS OF ANY AND ALL USERS (INCLUDING UNAUTHORIZED
              USERS), WHETHER SUCH ACTS OR OMISSIONS OCCUR DURING THE USE OF THE
              SERVICES OR OTHERWISE.
              <br />
              <br />
              Utiva may, at its sole discretion, choose to monitor User Content
              for inappropriate or illegal behavior, including through automatic
              means, provided however, that Utiva reserves the right to treat
              User Content as content stored at the direction of users for which
              Utiva will not exercise editorial control except when violations
              are directly brought to Utiva’s attention.
              <br />
              <br />
              You understand that Utiva is not responsible for the accuracy,
              usefulness, safety, appropriateness of, or infringement of any
              intellectual property rights of or relating to this Content
              (including but not limited to the User Content). Although Users
              must agree to these Terms, it is possible that other Users
              (including unauthorized users) may post or transmit offensive or
              obscene materials and that you may be involuntarily exposed to
              such offensive or obscene materials. You hereby waive any legal or
              equitable rights or remedies you have or may have against us with
              respect thereto.
              <br />
              <br />
              It is also possible for others to obtain Personal Data about you
              due to your use of the Platform, including through any User
              Content that you make available through your account. Anyone
              receiving or viewing User Content you share with other Users may
              use information you provided through such User Content for
              purposes other than what you intended. We are not responsible for
              the use of any Personal Data that you disclose on the Platform or
              through any User Content by any third party. By making any
              information available through the Platform you acknowledge that
              you understand and have agreed to such risks.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Restrictions
            </h3>
            <p>
              <strong>User Content Restrictions</strong>
              Utiva has no obligation to accept, display, or maintain any User
              Content. Moreover, Utiva reserves the right to remove and
              permanently delete any User Content uploaded by you, without
              notice and for any reason. You are and shall remain at all times
              fully and solely responsible for any User Content that you upload
              to the Services. You represent and warrant that any User Content
              that you upload (i) complies with applicable law; (ii) does not
              infringe or violate any third-party intellectual property rights,
              privacy or publicity rights, or moral rights; and (iii) that you
              have all necessary rights and authorities to submit such User
              Content.
              <br />
              <br />
              Without limiting the foregoing, you agree that you will not
              transmit, submit or upload any User Content to the Platform and/or
              Services or act in any way that: (i) restricts or inhibits use of
              the Services; (ii) imposes an unreasonably or disproportionately
              large load on our infrastructure; (iii) violates the legal rights
              of others, including defaming, abuse, stalking or threatening
              Users; (iv) infringes (or results in the infringement of) the
              intellectual property rights, moral rights, publicity, privacy, or
              other rights of any third party; (v) is (or you reasonably believe
              or should reasonably believe to be) stolen, illegal, counterfeit,
              fraudulent, pirated, violent or unauthorized, or in furtherance of
              any illegal, counterfeiting, fraudulent, pirating, unauthorized,
              or violent activity, or that involves (or you reasonably believe
              or should reasonably believe to involve) any stolen, illegal,
              counterfeit, fraudulent, pirated, or unauthorized material; (vi)
              does not comply with all applicable laws, rules and regulations;
              or (vii) posts, stores, transmits, offers, or solicits anything
              that contains the following, or that you know contains links to
              the following or to locations that in turn contain links to the
              following: (a) material that we determine to be offensive
              (including material promoting or glorifying hate, violence,
              bigotry, or any entity (past or present) principally dedicated to
              such causes or items associated with such an entity), (b) material
              that is racially or ethnically insensitive, defamatory, harassing
              or threatening, (c) pornography or obscene material, (d) any
              virus, worm, trojan horse, or other harmful or disruptive
              component; or (e) anything that encourages conduct that would be
              considered a criminal offense, give rise to civil liability,
              violate any law or regulation or is otherwise inappropriate or
              offensive.
            </p>
            <p>
              <strong>Use Restrictions</strong>
              You may not use the Platform and/or Services for any illegal,
              fraudulent or unauthorized purposes. You may not use the Services
              to violate intellectual property laws, or any other any laws in
              your jurisdiction that are applicable to you. You may not use the
              Services for personal, household, family, consumer or other
              non-commercial purposes. You may only use the Services for your
              bona fide internal business purposes in accordance with the terms
              hereof. You may not use the Services to solicit or provide
              services for the benefit of (a) an individual, organization, or
              country that is blocked or sanctioned by the United States,
              including those identified on the United States Office of Foreign
              Asset Control (OFAC); (b) any unaffiliated third parties; or (c)
              any other services not for the benefit of a User.
              <br />
              <br />
              Further, you may not (i) use the Platform and/or Services to
              solicit or provide services involving or related to any of the
              Prohibited User Activities set forth in the Prohibited Activity
              List; or (ii) do or attempt to do any of the Prohibited Uses set
              forth in the Prohibited Activities List. If we suspect or
              determine that you are using the Services in any many related to
              any Prohibited User Activities and/or Prohibited Uses, we reserve
              the right to disable your Account, freeze any funds in your
              Account, and report your activity to our financial services
              providers and other regulatory authorities with jurisdiction over
              us or you. To the extent permitted by applicable law, we will
              provide you with notice of any of the foregoing promptly following
              taking any such action.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Data Protection
            </h3>

            <p>
              You acknowledge and agree that Utiva may collect and process your
              Personal Data in connection with your receipt of the Services.
              Utiva shall process such Personal Data in accordance with Utiva’s
              Data Processing Addendum which forms an integral part of the Terms
              of Service. The Data Processing Addendum is available here (the
              “DPA”).
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Payments
            </h3>

            <p>
              <strong>Payment Plans</strong>
              If you are a Client, certain Services are subject to the payment
              of the fees to Utiva detailed on the Utiva Platform. You may
              access and use such Services by selecting from one of our payment
              plans ("Payment Plan(s)"). Payment Plan details are set forth on
              our Pricing Page. You hereby agree to make payment to Utiva of
              such fees detailed on the Platform in accordance with your
              applicable Payment Plan. If you are using our Platform in
              connection with Consultant Services provided by Utiva EOR
              Partners, your use of the Platform is not subject to the payment
              of any fees to Utiva. Please note additional fees may apply
              pursuant to the applicable Consultant Services Agreement. The
              payment plan will be charged at the moment you issue a payment to
              a Contractor or an employer or record on the platform as an
              additional amount. A separate invoice will be generated and will
              be located in the billing section. If you pay for your Payment
              Plan using a debit method, wire transfer or credit card (“Card”),
              the following terms apply:
              <br />
              Foreign Transaction Fees. You acknowledge that for certain
              transactions, your Card’s issuing bank may charge a foreign
              transaction fee or other charges.
              <br />
              Declined Payment. If payment is declined due to expiration of the
              Card, insufficient funds, or otherwise, Client remains responsible
              for any amounts not remitted to Utiva and Utiva may, in its sole
              discretion: (i) continue presenting the Card once it has been
              updated by Client (if applicable) or (ii) terminate this
              Agreement.
              <br />
              In the case where you have auto payments activated on Utiva, we
              will automatically charge you your payment plan on top of the
              payments Client will be issuing.
            </p>
            <p>
              Utiva may modify your Payment Plan fees at any time and in its
              sole discretion upon at least 30 days advance notice to you before
              the end date of your next billing cycle. Changes to Payment Plans
              will become effective at the end of the next billing cycle
              following your receipt of notice of the change. If you do not
              agree to the fee change, you must terminate your Payment Plan at
              least 5 days prior to the end of your next billing cycle by
              sending Utiva notice of termination to info@utiva.co. Your
              continued use of the Services after the Payment Plan change
              becomes effective constitutes your consent to the new Payment Plan
              terms.
              <br />
              <br />
              Payment Plan fees are stated exclusive of any sales tax,
              value-added taxes, use or withholding tax or other governmental
              assessments of any nature in your jurisdiction (collectively,
              “Taxes”). You are responsible for paying all Taxes associated with
              your Payment Plan, except for those taxes based on our net income.
              Should any payment for the Services be subject to withholding tax
              by any government, you will reimburse us for such withholding tax.
              <br />
              <br />
              Utiva may, at its sole discretion, offer a Payment Plan with a
              free trial for a limited period of time ("Free Trial").To
              participate in a Free Trial, you must select a Payment Plan and
              provide a valid payment method, however, we will not charge your
              payment method unless you continue using the Services after the
              Free Trial is over. If you are on a Free Trial, you may cancel at
              any time until the last day of your Free Trial by emailing
              info@utiva.co If you or Utiva cancel your Free Trial, you
              acknowledge and agree that we may delete all Contracts, Forms,
              Content, and all other data associated with your Account during
              your Free Trial. If you do not cancel your Free Trial, then your
              Free Trial will automatically be converted to a paid Payment Plan
              at the end of the trial period, and you hereby authorize us to
              charge the payment method your provided us in accordance with your
              Payment Plan To cancel your Payment Plan after your Free Trial has
              ended and your Payment Plan has begun, you must notify us by
              sending Utiva notice of termination to at least 5 days prior to
              the end of your next billing cycle. At any time and without
              notice, Utiva reserves the right to (a) modify the terms of any
              Free Trial offer, (b) withdraw any Free Trial offer, or (c) cancel
              any Free Trial Payment Plan at any time.
              <br />
              <br />
              Your payment obligations under your Payment Plan for each billing
              cycle may not be cancelled after a billing cycle has commenced,
              and you will not receive a partial refund if you stop using the
              Services and terminate your Payment Plan before the end of a
              billing cycle. All fees paid by you to Utiva are non-refundable
              and there are no credits for partially used Payment Plan periods.
              We may consider certain refund requests on a case-by-case basis in
              our sole discretion.
            </p>
            <p>
              <strong>Payment Methods</strong>
              Except for Utiva’s role as a limited payment agent for Contractors
              as set forth in Section 10.4, Utiva does not and will not provide
              banking, deposit taking, stored value, escrow, insurance or any
              other financial service to Users. To facilitate payments through
              the Service, Utiva partners with a variety of Payment Service
              Providers, including payment gateways, money transmitters, wallet
              providers, credit and debit card payment processors, merchant
              acquirers, and merchant acquiring banks. See our Payments Page for
              more information on supported third-party payment methods in your
              location. Depending on your location, some payment methods may not
              be available to you, and we cannot guarantee that you will be able
              to use any payment method in connection with the Services even in
              locations where your preferred payment method is available for
              use. Depending on the desired payment method, Users may be
              required to enter into a separate agreement with the applicable
              Payment Service Provider (“Payment Service Provider Agreement”).
              Your use of any payment method is subject to the terms and
              conditions of the applicable Payment Service Provider’s rules and
              regulations and your agreement with them, and we will not be
              liable to you for any losses you suffer in connection with your
              use of any third-party payment services. Utiva is not a party to
              your Payment Service Provider Agreement and will not intervene in
              any disputes related to payments you make or receive using any
              third-party payment method. Utiva also supports Client payments
              via Automated Clearing House (“ACH”) transfer, which will be
              subject to the National Automated Clearing House Association
              (“NACHA”) Operating Rules, and by wire transfer. Users are solely
              responsible for payment of insufficient funds fees, overdraft
              fees, wire transfer fees or other bank fees that you or we incur
              in connection with ACH payments, Payment Service Provider
              transaction fees, taxes, and any other third-party payment method
              fees or charges. Utiva does not charge Contractors any fees for
              payments processed through the Platform regardless of payment
              method., however, Utiva is not responsible for any transaction
              fees, foreign exchange fees or any other fee imposed separately on
              a Contractor by a Payment Service Provider or by Contractor’s own
              financial institution.
            </p>
            <p>
              <strong>Compliance</strong>
              Solely to protect against money laundering, terrorist financing,
              fraud, unauthorized transactions or as otherwise required by
              applicable law, Utiva and Payment Service Providers will collect,
              store and analyze User payment information. For more information
              on how we use your payment information, please see our Privacy
              Policy for more information here. Payment Services Providers may
              also collect payment information necessary for processing User
              payments. Except for payment amount and payment status details
              stored in your dashboard, Utiva does not have access to payment
              information provided to Payment Services Providers, and such
              information will be subject to the privacy policy of each Payment
              Service Provider.
              <br />
              <br />
              By using any payment method and providing payment information to
              Utiva or to any Payment Service Provider, you represent and
              warrant that you are the owner, or authorized representative of
              the owner of the bank account or payment method account you use to
              send or receive payments, and that you are legally authorized to
              send or receive payments using such accounts.
              <br />
              <br />
              All payment services, including withdrawal services will be
              provided by Utiva’s Payment Service Provider.
            </p>
            <p>
              <strong>Payment Service Providers</strong>
              If you make or receive payments using a Payment Service Provider,
              you acknowledge and agree that you will comply with any Payment
              Service Provider terms of service or other agreement between you
              and the Payment Service Provider. For example, customers in the
              United States may receive payment processing services by our
              partner, subject to their terms found{" "}
              <a
                href="https://info.alviere.com/hubfs/Deel/Alviere%20Domestic%20USA%20Payment%20Service%20Agreement_020424.docx.pdf"
                target="_blank"
                rel="noreferrer"
                className="text-primary"
              >
                here
              </a>
              , as well as their privacy policy found{" "}
              <a
                href="https://alviere.com/privacy-policies/us-privacy-policy"
                target="_blank"
                rel="noreferrer"
                className="text-primary"
              >
                here
              </a>
              . We will not be held liable for any losses or damages, direct or
              indirect, pecuniary or non-pecuniary, resulting from the actions
              or omissions of any Payment Service Provider, and we expressly
              disclaim any responsibility in this regard. Any fees for use of a
              Payment Service Provider to make payments or receive payments will
              be borne by you. Any fees imposed on you by a Payment Service
              Provider are in addition to any fees payable to Utiva.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Management Services
            </h3>
            <p>
              The following terms apply to any Management Services hereunder:
            </p>

            <p>
              <strong>Contracts</strong>
              <ol className="list-inside list-decimal">
                <li>
                  Contract Dashboard. Users can use the Platform to create and
                  manage Contracts. Each Contract is assigned a unique Contract
                  ID. Users can organize Contracts on the Utiva dashboard
                  according to Contract type, execution status, Client,
                  Contractor and ID.
                </li>
                <li>
                  Contract Templates. Utiva provides Users with three Contract
                  templates ("Templates"):
                  <br />
                  "Fixed Contract" - open-ended contracts featuring fixed
                  payments at regular intervals for recurring services.
                  <br />
                  "Pay As You Go Contract" closed-end contracts featuring
                  payment of a predetermined amount upon acceptance of services
                  by the Client, partial payment can be made at regular
                  intervals. "Milestone Contract" - contracts featuring fixed
                  payments upon achievement of pre-defined milestones.
                </li>
                <li>
                  Contract Generation. Users can set contract terms by selecting
                  from pre-set parameters using Utiva's Contract generation
                  tool. Parameters include Client and Contractor names and
                  addresses, Contract type, payment type, payment cycles,
                  special clauses and jurisdiction. Users can also use the web
                  form to input project scope, Statements of Work, and other
                  Contract details.
                </li>
                <li>
                  Contract Execution. Users can use Utiva's secure e-sign
                  functionality to execute agreements. Contracts executed on
                  Utiva are legally binding.
                </li>
                <li>
                  Pre-Existing Agreements. Users can also use the Utiva
                  dashboard to upload and manage agreements created and executed
                  by Clients and Contractors outside the Platform. Users can
                  extract parameterized terms from pre-existing agreements such
                  as payment amount, type, and due date, and amend pre-existing
                  agreements to insert new terms, all from within the same
                  interface.
                </li>
                <li>
                  Contract Management. Users can securely store and manage
                  multiple Contracts in their Accounts organized by type,
                  counterparty, and completion status. Users can limit access to
                  Contracts and Contract information to specific persons,
                  companies, or groups. Users can track each Contract according
                  to CIient approval status and payment status. Users may also
                  modify or terminate Contracts through the Platform.
                </li>
                <li>
                  Prohibited Contract Terms. If we determine that the content of
                  a Contract violates these Terms or the Prohibited Activity
                  List, we may remove the Contract from your Dashboard without
                  prior notice to you. We may also suspend or close your
                  Account. To the extent permitted by applicable law, we will
                  provide you with notice of any of the foregoing promptly
                  following taking any such action. Contracts removed from your
                  Account may not be edited or restored.
                </li>
              </ol>
            </p>
            <p>
              <strong>Disputes and Contract Cancellations</strong>
              <ol className="list-inside list-decimal">
                <li>
                  Reversals and Disputes. Clients should not initiate payments
                  until Contractors have met the acceptance criteria or other
                  requirements detailed in a Contract. Except to satisfy the
                  compliance obligations set forth in Section 9.3, Utiva cannot
                  reverse payments or withhold funds from Contractors, and
                  cannot cause Payment Service Providers to reverse payments or
                  withhold funds from Contractors once funds have been received
                  by Utiva or the relevant Payment Service Provider. If a Client
                  is not satisfied with the service provided by a Contractor or
                  makes a payment in error, the Client must initiate a refund,
                  reversal or other payment dispute process directly with the
                  Contractor or with the relevant Payment Service Provider.
                  Utiva will not adjudicate payment or Contract disputes between
                  Users regardless of payment method or Contract status under
                  any circumstances, and Users are solely responsible for
                  dispute settlement. Refunds and reversals of payments made
                  through a Payment Service Provider are subject to the terms
                  and conditions of your Payment Service Provider agreement.
                </li>
                <li>
                  User-Initiated Contract Cancellation. If a Client owes
                  outstanding payments to a Contractor after completion of work,
                  or if a Client is dissatisfied with the service provided by a
                  Contractor, the Client or Contractor may initiate cancellation
                  of the Contract through the Utiva dashboard. Cancellation of a
                  Contract through the Utiva dashboard has no bearing whatsoever
                  on the merits of a Contract dispute, or the interpretation of
                  the terms of, or legality or validity of a Contract. The
                  Services record the initiator of, and the time and date of the
                  cancellation. If you delete a Contract, the deleted Contract
                  and all Content therewith will immediately become inaccessible
                  to other Users, after which we will permanently delete the
                  Contract from our servers. Users may not initiate or request
                  payment for services related to, or alter the status of a
                  Contract after it has been cancelled.
                </li>
                <li>
                  Contract Cancellation by Utiva. Utiva may cancel a Contract in
                  the event of suspicious Account activity or Account
                  compromise; fraud, harassment, and threats; unfair, deceptive,
                  or abusive acts or practices; illegal acts; at the direction
                  of a regulatory authority; or for any other violation of these
                  Terms of Service. Utiva may also cancel a Contract if a User
                  is no longer a Utiva Accountholder in good standing. Please
                  contact us if you believe a Contract or another User is in
                  violation of these Terms.
                </li>
              </ol>
            </p>
            <p>
              <strong>Tax Forms & Invoices</strong>
              <ol className="list-inside list-decimal">
                <li>
                  Form and Invoice Generation. We use User data to generate
                  required tax documents for Users. For Clients and Contractors
                  in the United States, we automatically generate forms W9,
                  W-8BEN and W-8BEN-E ("Forms") to be dated and signed by the
                  Contractor. We do not guarantee that information on any Form
                  generated by the Platform is accurate or correct, or that
                  Users have selected the appropriate Form for the type of tax
                  return they submit. Users should verify the accuracy and
                  completeness of the information on the forms before submitting
                  them to other Users or to any tax authority. By using Utiva to
                  generate and sign Forms, you represent and warrant that the
                  information you have provided is accurate and complete, and
                  specifically to the terms of Section 12 regarding electronic
                  signatures.
                  <br />
                  <br />
                  We use a third party service to enable you to file your 1099
                  at the end of each tax year.
                  <br />
                  <br />
                  We also generate and submit invoices to Clients on behalf of
                  Contractors when payment is due. Contractors may also use
                  Utiva to automatically submit invoices for recurring services,
                  or schedule delivery of invoices according to project
                  milestones set for in Contracts.
                </li>
                <li>
                  Tax Compliance Services and Related Disclaimers for Clients.
                  <br />
                  <br />
                  Certain Clients may choose to use the "Tax Compliance
                  Services" portion of the Services, as described more fully on
                  the tax compliance page. If you use the Tax Compliance
                  Services, you agree to the terms of this subsection 10.3.2.
                  <br />
                  <br />
                  Utiva does not provide tax, legal or accounting advice to
                  Users. We will do our best to provide you with the information
                  you need to make your own decisions about compliance with
                  applicable U.S. tax laws. If you have questions after
                  reviewing the Forms we generate and other tax withholding
                  rates, or any other tax information provided by Utiva or the
                  Internal Revenue Service ("IRS"), you should consult your own
                  tax, legal or accounting advisors prior to completing or
                  submitting a Form or paying an invoice. The disclaimers in
                  this paragraph apply equally to you as a Client or a
                  Contractor.
                  <br />
                  <br />
                  Information regarding tax withholding rates calculated by
                  Utiva and provided to you through the Tax Compliance Services
                  is dependent on accurate and truthful information regarding
                  Users and their situation or circumstances. Utiva will in no
                  way be liable to you or any third party (including but not
                  limited to any taxation authority) for any losses or
                  penalties, pecuniary or otherwise, arising from a User
                  entering inaccurate or false information, whether purposefully
                  or not, or misrepresenting their business type, taxation or
                  employment status in any manner.
                  <br />
                  <br />
                  Information regarding tax withholding rates calculated by
                  Utiva and provided to you is further dependent on your
                  specific business circumstances and tax form settings you
                  provide to Utiva, the income type provided by Clients in
                  payments to Contractors as well as any other tax-related
                  information. Users may also owe indirect taxes (such as VAT or
                  GST) depending on the applicable tax laws in the jurisdiction
                  where they are domiciled, in addition to the payment amount
                  indicated in an invoice. Users agree that they are solely
                  responsible for any obligation to deduct or withhold taxes and
                  for any other tax requirements applicable to them. You also
                  agree that the amount of any invoice, and any fees you owe for
                  the Services are net of direct or indirect taxes, levy,
                  withholding tax or deductions.
                </li>
              </ol>
            </p>

            <p>
              Specifically, if you are a Contractor and use the Tax Compliance
              Services, you acknowledge and agree to the following (the
              “Contractor Tax Compliance Obligations”):
              <ul className="list-inside list-disc">
                <li>
                  You are solely responsible for remitting any amounts owed by
                  you to the IRS or other governmental authority in a timely
                  fashion according to IRS rules and regulations and your normal
                  accounting practices;
                </li>
                <li>
                  You will update your settings to reflect any material changes
                  to your situation or circumstances that may affect your tax
                  withholding rates, within 30 days of becoming aware of such
                  material change; and
                </li>
                <li>
                  You will inform Utiva of any errors or omissions you discover
                  that would affect a tax withholding calculation, within 30
                  days of becoming aware of such error or omission.
                </li>
              </ul>
            </p>
            <p>
              Utiva will in no way be liable to you, any taxation authority,
              Client, or any third party for any losses or penalties, pecuniary
              or otherwise, arising from your failure to adhere to these
              Contractor Tax Compliance Obligations.
            </p>

            <p>
              <strong>Appointment of Utiva as Payment Agent</strong>
              Contractors and Client employees (each a “Payee” for purposes of
              this Section) hereby appoint Utiva as their limited authorized
              payment collection agent (“Payment Agent”) solely for the purpose
              of facilitating the receipt of payments (via its Payment Services
              Provider, if applicable) from Clients for Services provided in
              connection with Contracts or their employment.
              <br /> <br />
              Payee agrees that payment received from Clients by Utiva will be
              considered the same as payment made directly to Payee, regardless
              of whether Utiva remits or fails to remit the payment to Payee.
              Payee also authorizes Utiva in its role as Payment Agent to:
              <br /> <br />
              hold, disburse and retain payments on behalf of Payee pursuant to
              these Terms of Service, or otherwise instruct Utiva’s Payment
              Service Providers to do so;
              <br /> <br />
              issue refunds to Clients at the request of Payee; and
              <br /> <br />
              manage Client credit and debit card chargebacks on behalf of
              Payee.
              <br /> <br />
              In accepting appointment as Payment Agent, Utiva assumes no
              liability whatsoever for any acts or omissions of Payees related
              to Contracts, employment, Forms, or these Terms of Service,
              failure by Payee to provide the Services in accordance with
              Contracts or their employment, or failure by Clients to make
              payments owed to Payee, and Payee understand that Utiva’s
              obligation to pay Payee is subject to and conditional upon Utiva’s
              actual receipt of payment from Clients. Payees further authorize
              Utiva to delegate its Payment Agent obligations under these Terms
              of Service to certain of its affiliated entities (“Affiliates”)
              both within and outside the United States; provided, that, Utiva
              will remain liable for discharge of its obligations under these
              Terms of Service by such Affiliates. Payee represents and warrants
              that they have carefully read and understood these Terms of
              Service and accept them fully. Clients’ payment obligations to
              Payee will be satisfied upon receipt of payment by Utiva (or its
              Payment Service Provider, as applicable), and Utiva (via its
              Payment Service Provider, as applicable) will be responsible for
              remitting funds to Payee in the manner described in these Terms of
              Service. In the event that Utiva (via its Payment Service
              Provider) does not remit any such amounts to a Payee, the Payee
              will have recourse for non-payment solely against Utiva, and not
              Clients. Payee agrees that Utiva may describe or otherwise reflect
              the terms contained herein in any terms of service, receipts,
              disclosures, or notices including, but not limited to, receipts
              provided to Clients that Utiva may deem necessary or prudent.
            </p>
            <p>
              <strong>Payments to Contractors</strong>
              Clients may pay Contractors in any of Utiva's supported
              currencies. Unless Client instructs Utiva otherwise, Client will
              be charged in the currency indicated on the applicable Contract,
              in the event a Client elects to pay in another currency, the
              exchange rate will be calculated using forward foreign exchange
              rates available to Utiva and the maturity of the forward will be
              selected based on payment date of the relevant Contract with such
              Contractor. In any event, the actual payment amount, in the actual
              payment currency will be clearly disclosed to the Client before
              the Client completes the payment. Contractors may elect to be paid
              in any one of Utiva's supported currencies. Payment Service
              Provider retail fees and rates will be passed through to the
              Contractor.
            </p>
            <p>
              <strong>Ownership of Deliverables</strong>
              Except as set forth in any Contract or statement of work between
              the Contractor and Client, Contractor agrees to grant all
              copyrights and all other intellectual property rights to the work
              and deliverables delivered to Clients in connection with Contracts
              (“Deliverables”), and the Contractor waives any and all moral
              rights to Deliverables. Deliverables will be considered
              work-for-hire under the U.S. Copyright Act. If Deliverables do not
              meet the requirements of work-for-hire or when the US Copyright
              Act does not apply, the Contractor will expressly agree to assign
              to Client the copyright to the Deliverables. All transfer and
              assignment of intellectual property to Client will be subject to
              full payment pursuant to the relevant Contract terms. Clients may
              not use the Deliverables if payment is not made in full or the
              Contract is cancelled for any reason. Notwithstanding the
              foregoing, for custom Deliverables (such as art work, design work,
              report generation, etc.), the Deliverables will be the exclusive
              property of the Client, and the Contractor assigns all rights,
              title and interest in the Deliverables to the Client. Contractors
              further confirm that whatever information they receive from the
              Client that is not in the public domain, will be kept confidential
              and will not be shared or used for any purpose whatsoever other
              than for the delivery of the Deliverables or performance of
              services for the Client pursuant to the Contract.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Premium Service Guarantee
            </h3>

            <p>
              <strong>Additional Terms of Service</strong>
              <ul className="list-inside list-disc">
                <li>
                  Subject to the terms and conditions set forth below and
                  provided that the Client maintains a subscription to a Utiva
                  Premium plan (each, a "Utiva Premium Plan") fully paid and in
                  good standing, Utiva will provide the Client with a "Service
                  Guarantee."
                </li>
                <li>
                  The Service Guarantee means that where Utiva incorrectly
                  supplies the Client with a Contractor which, due to Utiva's
                  error, is misclassified as an independent contractor (or its
                  equivalent) under applicable law (a "Misclassification") and
                  the Client suffers Losses during the term of a Relevant
                  Contract as a result of Utiva's actions, then Utiva agrees to
                  compensate the Client for the consequences of its failure to
                  deliver the correct service, subject to the limits set out in
                  Section 11.5 below.
                </li>
                <li>
                  For the purposes of this Service Guarantee, "Losses" means:
                  (i) verifiable expenses limited to legal defense costs payable
                  by a Client ("Legal Costs") in connection with the
                  Misclassification of a Contractor contracted using the Utiva
                  Platform (a "Relevant Contract"); (ii) liabilities, interest
                  and penalties payable by the Client to tax authorities ("Tax
                  Penalties") as a result of the Client being deemed liable by a
                  government or administrative body or a court of competent
                  jurisdiction in respect of the Misclassification of a Relevant
                  Contract; and (iii) other costs incurred by the Client ("Other
                  Costs") as a result of the Client being deemed liable by a
                  government or administrative body or a court of competent
                  jurisdiction in respect of the Misclassification of a Relevant
                  Contract, each subject to the maximum amounts set forth below
                  and the other terms and conditions contained herein.
                </li>
                <li>
                  Utiva will not be required to compensate the Client for its
                  failure to deliver the correct service where any Losses are
                  incurred following the termination of a Relevant Contract or
                  the cancellation of a Utiva Premium Plan.
                </li>
              </ul>
            </p>

            <p>
              <strong>What is not guaranteed</strong>
              Notwithstanding anything to the contrary in this Service
              Guarantee, Utiva will not be required to compensate the Client
              where its Losses arise out of or result from:
              <br /> <br />
              <ul className="list-inside list-disc">
                <li>
                  actions taken by the Client, its Contractors, or actions by
                  any third party that are outside the scope of Utiva's
                  services;
                </li>
                <li>
                  the use of any contract other than the latest contract
                  provided or approved in writing by Utiva to the Client in
                  respect of the Contractor (as amended or updated from time to
                  time);
                </li>
                <li>
                  the Client's obligation to have a permanent establishment in
                  the country where the Contractor is a permanent resident or
                  where the Contractor is delivering his, her or its services;
                </li>
                <li>
                  a change in the applicable laws of a jurisdiction if a
                  Client's Questionnaire (defined below) for the specific
                  Contractor giving rise to such Losses is not considered
                  "passed" in accordance with the section titled "Client
                  Questionnaire" below;
                </li>
                <li>
                  criminal, fraudulent, negligent or malicious acts of the
                  Client, any Contractor or third party;
                </li>
                <li>
                  violations of laws applicable to the Client or other employers
                  that are not related to the Misclassification of a Contractor;
                </li>
                <li>
                  prior or pending litigation of the Client, or events to which
                  the Client had prior notice that such events could result in
                  litigation against the Client;
                </li>
                <li>
                  consequential damages, special damages, interruption of
                  business, opportunity costs, loss of use, or loss associated
                  with unauthorized access to data;
                </li>
                <li>
                  a change in applicable law that occurs after the expiration or
                  termination of the Client's Utiva Premium Plan;
                </li>
                <li>
                  any Relevant Contract after the expiration of the Term or for
                  which the Client has terminated its Utiva Premium Plan;
                </li>
                <li>
                  any Contractor engaged after the expiration of the Client's
                  Utiva Premium Plan;
                </li>
                <li>
                  the misrepresentation of any material fact submitted to Utiva
                  by the Client.
                </li>
              </ul>
            </p>
            <p>
              <strong>Term and Termination</strong>
              The term of a Utiva Premium Plan will begin on the date of
              purchase of such Utiva Premium Plan for a Relevant Contract (the
              “Effective Date”) and end on the earlier of (i) the duration of
              the underlying Relevant Contract, or (ii) one (1) year (the
              “Initial Term”), unless terminated earlier as provided herein.
              <br /> <br />
              After the Initial Term, the Plan will automatically renew in
              additional one (1) year increments (each a “Renewal Term” and
              collectively, the Initial Term and the Renewal Term are referenced
              as the “Term”) unless terminated in writing as provided below at
              least 30 days prior to the expiration of the Initial Term or any
              Renewal Term (or such length as otherwise required by applicable
              law).
              <br /> <br />
              The Utiva Premium Plan will terminate immediately upon the earlier
              of: (i) the expiration of the Term of the Utiva Premium Plan or
              underlying Relevant Contract; (ii) the Client’s non-payment or
              failure to make payment when due for any reason; or (iii) the date
              that the Client cancels, downgrades, or terminates the underlying
              Relevant Contract or Utiva Premium Plan as provided herein. A
              Utiva Premium Plan may be terminated by as follows:
              <br /> <br />
              <ul className="list-inside list-disc">
                <li>
                  By Utiva: Utiva may at any time immediately terminate the
                  Client's Utiva Premium Plan in its entirety or terminate its
                  applicability to any specific Relevant Contract for
                  non-payment, fraud, or misrepresentation without prior written
                  notice to Client. In addition, Utiva may terminate the
                  Client's Utiva Premium Plan for convenience at any time by
                  providing the Client with 30 days' prior written notice.
                </li>
                <li>
                  By the Client: The Client may terminate a Utiva Premium Plan
                  at the end of any Term by providing Utiva with at least 30
                  days' written notice prior to the expiration of the current
                  Term through the Utiva Platform or at info@utiva.co.
                </li>
              </ul>
            </p>
            <p>
              <strong>Notice of Loss</strong>
              The Client will give Utiva prompt written notice by email at
              info@utiva.co (a “Notice”) within 5 days of the date of discovery
              of any facts or situation which the Client has reasonably
              determined could give rise to a Loss under this Service Guarantee,
              including any change of law, complaints, communications or claims
              made or threatened by a Contractor or governmental authorities
              that could result in legal proceedings related to the
              Misclassification of a Contractor. Each Notice must contain a
              description of the issue and the nature and amount of the related
              Losses (to the extent that the nature and amount of the Losses are
              known at the time, failing which a reasonable estimate of the
              potential amount of the Losses will be provided). The Client will
              furnish promptly to Utiva copies of all material documents
              relating to the Losses. Any liability Utiva may have to compensate
              the Client pursuant to this Service Guarantee is conditional upon
              the Client promptly delivering the Notice and related documents
              under this Notice of Loss section.
            </p>

            <p>
              <strong>Utiva Premium Limitation of Liability</strong>
              <ol className="list-inside list-decimal">
                <li>
                  For purposes of this Section 11, Utiva's maximum liability for
                  all Losses in respect of any Utiva Premium Plan will be
                  determined as set forth in each subsection below. If the
                  Client has subscribed for and maintained in good standing the
                  Utiva Premium Plan, Utiva agrees to compensate the Client
                  under this Service Guarantee to the extent it considers that
                  its failure to provide the requisite service has led the
                  Client to suffer Losses, subject to the maximum limits set out
                  below:
                  <br /> <br />
                  <ul className="list-inside list-disc">
                    <li>
                      Maximum Liability for Legal Costs and Tax Penalties. Legal
                      Costs and Tax Penalties up to a maximum aggregate amount
                      of $25,000 per Contractor.
                    </li>
                    <li>
                      Maximum Liability for Other Costs. Other Costs up to a
                      maximum aggregate amount of $10,000 per Contractor.
                    </li>
                  </ul>
                </li>

                <li>
                  FOR PURPOSES OF THIS SECTION 11 AND IN ADDITION TO THE LIMITS
                  ABOVE, EXCEPT AS OTHERWISE SPECIFICALLY STATED IN THIS
                  AGREEMENT, Utiva WILL NOT BE LIABLE FOR ANY INDIRECT,
                  CONSEQUENTIAL, OR ECONOMIC DAMAGES OR FOR LOSS OR DAMAGES TO
                  ANY PERSON OR PROPERTY RELATING TO OR ARISING FROM THIS
                  AGREEMENT. IN ADDITION TO THE LIMITS STATED ABOVE, Utiva'S
                  MAXIMUM LIABILITY TO ANY ONE CLIENT, REGARDLESS OF THE NUMBER
                  OF RELEVANT CONTRACTS SUCH CLIENT HAS OUTSTANDING, WILL NOT
                  EXCEED $250,000 IN THE AGGREGATE.
                </li>
              </ol>
            </p>
            <p>
              <strong>
                Utiva Premium Utiva Premium Representations and Warranties of
                the Client
              </strong>
              For purposes of this Section 11, Utiva will not be liable for any
              Losses based upon or arising out of any inaccuracy in or breach of
              any of the representations or warranties of the Client contained
              in the Questionnaire or on the Utiva Platform if the Client knew
              or should have known that any such representation or warranty is,
              was or might be inaccurate. The Client agrees to produce or make
              available for inspection, at such reasonable times and places
              designated by Utiva or any agent, all evidence of loss, damage,
              cost, ownership or other Loss-related information. Notwithstanding
              the foregoing, Utiva will have no liability to the Client or any
              other person because of any inspection or failure to inspect.
            </p>
            <p>
              <strong>Client Questionnaire</strong>
              To be eligible for this Service Guarantee, the Client’s account
              must be compliant with the Terms of Service, including, but not
              limited to, completing and passing Utiva’s due diligence support
              questionnaire (the “Questionnaire”), as follows:
              <br />
              <br />
              <ul className="list-inside list-disc">
                <li>
                  The Client must pass the Questionnaire at the time that the
                  Contractor is hired by the Client, unless otherwise approved
                  in writing by Utiva.
                </li>
                <li>
                  A Questionnaire is considered "expired" or "failed," and
                  therefore not passed, and must be retaken by the Client for
                  each Relevant Contract at the earlier of:
                  <br />
                  <br />
                  <ul className="list-inside list-disc">
                    <li>
                      12 months after the Questionnaire is previously taken;
                    </li>
                    <li>
                      90 days after there is a change in applicable law that is
                      communicated by Utiva to the Client;
                    </li>
                    <li>
                      immediately upon the Client's communication with Utiva in
                      accordance with the Section above entitled "What is not
                      guaranteed"; or
                    </li>
                    <li>
                      immediately upon a change in the applicable law that would
                      have been communicated by Utiva to the Client but for the
                      fact that the Client's Utiva Premium Plan expired prior to
                      the change in the applicable law.
                    </li>
                  </ul>
                </li>
                <li>
                  The Client must immediately communicate to Utiva in writing by
                  email at info@utiva.co of any change in the scope of the
                  contractual relationship with any of its Contractors (for
                  example, a change in responsibilities, country, etc.), and
                  must retake and pass the Questionnaire.
                </li>
              </ul>
            </p>
            <p>
              <strong>Changes to Utiva Premium Plan</strong>
              Utiva reserves the right to make changes to this Service Guarantee
              or the terms and conditions of any Utiva Premium Plan, or adjust
              Utiva Premium Plan pricing, in any manner and at any time as we
              may determine in our sole and absolute discretion. Utiva will
              provide you written notification of any material changes to your
              Utiva Premium Plan, including any adjustments to the price of your
              Utiva Premium Plan, at least 10 days in advance of the
              implementation of such changes. After notice of a material change,
              you may terminate your Utiva Premium Plan by providing written
              notice to us within the 10-day notice period prior to the
              Effective Date of the change. Except as otherwise expressly
              provided for herein, any price changes or changes to your Utiva
              Premium Plan will take effect following such 10-day notice period.
              If we discontinue your Utiva Premium Plan, our liability will be
              limited to resolving any existing complaints and issues as to
              Utiva's service which is already underway at the time that the
              Utiva Premium Plan is discontinued.
            </p>
            <p>
              <strong>Utiva’s Control of Defense</strong>
              Utiva may, in its reasonable discretion, first try to resolve any
              issues with the governmental or administrative body or a court
              directly, if applicable. Utiva may assume, at its sole option,
              control of the defense, appeal or settlement of any third-party
              claim that is reasonably likely to give rise to a Loss under this
              Service Guarantee. If Utiva elects to assume control of the
              defense, Utiva will provide written notice to the Client
              acknowledging responsibility for the defense and undertaking,
              through reputable independent legal counsel of its own choosing,
              the settlement or defense thereof. If Utiva assumes control of the
              defense, the Client will reasonably cooperate with Utiva in
              connection therewith. Utiva may condition final payment of costs
              under this section on execution of a settlement agreement,
              including a release of claims against Utiva or any other
              applicable party, and an obligation to keep confidential the
              reimbursement or settlement amount and circumstances.
            </p>
            <p>
              <strong>Miscellaneous</strong>
              <ul className="list-inside list-disc">
                <li>
                  Except as prohibited by law, Utiva may modify or terminate
                  this Service Guarantee at any time, including to the extent
                  required to comply with applicable law, and such modification
                  or termination will apply to incidents occurring after the
                  effective date of the modification or termination. You
                  acknowledge and agree that all benefits made available to you
                  under this Service Guarantee will immediately terminate in the
                  event you initiate any legal action or claim against Utiva,
                  its officers, directors, employees, contractors, agents, or
                  affiliates concerning a compensation payment under this
                  Service Guarantee.
                </li>
                <li>
                  Amounts payable under this Service Guarantee are not a
                  replacement for applicable insurance coverage.
                </li>
                <li>
                  Subject to the terms of this Service Guarantee, the benefit
                  provided herein will remain in full force and effect until the
                  earlier of: (i) the date of termination of the Relevant
                  Contract; (ii) the Client's Questionnaire is expired; (iii)
                  the date that the Client cancels or downgrades its Utiva
                  Premium Plan; (iv) 24 months following the expiration of the
                  Client's Utiva Premium Plan; or (v) Utiva's termination of the
                  Service Guarantee.
                </li>
              </ul>
            </p>
            <p>
              <strong>Utiva Premium Services</strong>
              In addition to the obligations of the parties described above, the
              Utiva Premium Plan includes access to the Utiva Premium newsletter
              as a Premium Service.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Utiva Engage
            </h3>
            <p>
              <strong>Presentation</strong>
              Subject to the terms hereof, as part of the Utiva Platform, Client
              may access Utiva’s cloud-based solution modules and features for
              managing career development, performance management, learning
              management, applicant tracking system, compensation and budgeting
              management, and job and position planning system via the internet
              (“Utiva Engage Services”).
              <br /> <br />
              The Client wishes to have access to Utiva Platform and use its
              online services solely in its internal business operations.
              <br /> <br />
              Subject to this Section 12, Utiva has agreed to grant access to
              the Client with certain features and plug-ins to the Utiva
              Platform which includes its online services as further described
              below and the Client has agreed to take and pay (if applicable)
              for Utiva Engage Services and provided that the Client complies
              with any and all of the requirements set forth in this Section
              12and the applicable provisions of the Terms of Service.
            </p>
            <p>
              <strong>Specific Definitions</strong>
              The following definitions apply solely to Utiva Engage Services
              and not to the Terms of Service.
              <br /> <br />
              Authorised Users: those Users which are workers and independent
              contractors of the Client and are authorised or added by the
              Client (or by Utiva at Client’s request) to use the Utiva
              Platform, as further described herein and for whom, where
              applicable, the Subscription Plan has been duly paid to Utiva by
              Client.
              <br /> <br />
              Subscription Plan: any fees payable by the Client to Utiva for the
              provision of the Utiva Engage Services selected by the Client as
              set out in the Pricing Page.
              <br /> <br />
              Effective Date: the date on which access was granted to the Utiva
              Engage Services by Utiva to the Client and/or to any of its
              Authorised Users.
            </p>
            <p>
              <strong>Services and Subscription</strong>
            </p>
            <p>
              <strong>Services provided through the Utiva Platform</strong>
              Subject to the Terms of Service and this Section 12, including,
              payment by the Client of the applicable Subscription Plan as
              further specified in the Pricing Page:
              <br /> <br />
              Subject to the Terms of Service and this Section 12, including,
              payment by the Client of the applicable Subscription Plan as
              further specified in the Pricing Page:
              <ul className="list-inside list-disc">
                <li>
                  Utiva hereby grants to the Client a non-exclusive,
                  non-transferable right and license, without the right to grant
                  sublicenses, to permit the Authorised Users to use the Utiva
                  Platform during the Subscription Term solely for the Client's
                  internal business operations.
                </li>
                <li>
                  The Client is authorized to access and use the Utiva Platform
                  and its Utiva Engage Services on a non-exclusive basis solely
                  for internal business purposes notably to facilitate the
                  onboarding process of its employees and/or independents
                  contractors, create reports about its such users, allow them
                  to submit documents and access other functionalities as may be
                  further described and offered within the Utiva Platform.
                </li>
                <li>
                  The Authorized User(s) will have access to specific
                  functionalities that can be expanded from time to time
                  relating to modules and features for managing career
                  development, performance management, learning management,
                  applicant tracking system, compensation and budgeting
                  management, and job and position planning.
                </li>
              </ul>
              <br /> <br />
              Utiva may, at its sole discretion and at any time, modify or
              discontinue providing the Services provided through the Utiva
              Platform or any part thereof without notice.
              <br /> <br />
              Utiva will charge the Client for any Authorised User added to the
              Utiva Platform as expressly specified in the Pricing Page which
              may be amended from time to time.
              <br /> <br />
              It is also agreed that the Client will be charged by Utiva for any
              additional purchase to be made within the Utiva Platform and/or in
              connection with any additional services.
            </p>

            <p>
              <strong>Authorized Users</strong>
              <ol className="list-inside list-decimal">
                <li>
                  Subject to the Terms of Service and its restrictions, the
                  Client may, from time to time during the Term, grant access to
                  any additional Authorised User to the applicable Utiva Engage
                  Services, only by adding Authorised Users in the Utiva
                  Platform and paying the applicable Subscription Plan.
                </li>
                <li>
                  It is agreed that the simple addition of any Authorised User
                  by the Client in the Utiva Platform will be considered as an
                  ordering of an additional User Subscription.
                </li>
                <li>
                  In relation to the Authorised Users, the Client undertakes
                  that:
                  <ul className="list-inside list-disc">
                    <li>
                      it will be solely responsible for the use, attempted use,
                      misuse by any users (including the Authorised Users) of
                      the Utiva Platform and any of its Utiva Engage Services.
                    </li>
                    <li>
                      solely Authorised Users will be authorised to use the
                      Utiva Platform.
                    </li>
                    <li>
                      it will not allow or permit any User Subscription to be
                      used by more than one individual Authorised User unless it
                      has been reassigned in its entirety to another individual
                      Authorised User, in which case the prior Authorised User
                      shall no longer have any right to access or use the
                      Services; and
                    </li>
                    <li>
                      each Authorised User shall keep a secure password for
                      their use of the Utiva Engage Services and that each
                      Authorised User shall keep their password confidential.
                    </li>
                  </ul>
                </li>
              </ol>
            </p>
            <p>
              <strong>Fees and Payment</strong>
              In order to use the Services, including the Utiva Platform
              described herein, the Client shall pay Utiva the applicable
              Subscription Plan set forth in the Pricing Page. Such fees shall
              be paid in accordance with the payment terms set forth in Section
              9 of the Terms of Service.
            </p>
            <p>
              <strong>Obligations of the Client</strong>
              <ol className="list-inside list-decimal">
                <li>
                  The Client shall protect the access authorisations as well as
                  identification and authentication information assigned to him
                  or to the users from access by third parties and shall not
                  disclose them to unauthorised persons.
                </li>
                <li>
                  The Client is obligated to indemnify Utiva against all claims
                  of third parties due to infringements of rights, which are
                  based on an unlawful use of the services by him or are made
                  with his approval. If the Client recognizes or must recognize
                  that such an infringement is imminent, the customer is
                  obligated to inform Utiva immediately.
                </li>
                <li>
                  The Client shall use possibilities provided by Utiva to
                  additionally secure its data in its original area of
                  responsibility.
                </li>
              </ol>
              <br /> <br />
              The Client shall protect the hardware and software in particular
              against unauthorized access by employees or other third parties,
              viruses, Trojans and other malware.
            </p>
            <p>
              <strong>Term and Termination</strong>
              The term of a Subscription Plan shall begin on the Effective Date
              and shall continue until terminated by Utiva or the Client as
              provided in this Section 12 (the “Subscription Term”).
              <br /> <br />
              Either party may terminate the Subscription Plan without cause and
              at any time upon giving 30 days’ prior written notice to the other
              party (the “Notice Period”). Such termination shall be effective
              commencing from the first day of the following calendar month.
              <br /> <br />
              The Subscription Plan may be terminated immediately by Utiva
              (without notice) upon: (i) the Client’s non-payment or failure to
              make payment when due for any reason; or (ii) in the event of
              suspicious Account activity or Account compromise; fraud,
              harassment, and threats; unfair, deceptive, or abusive acts or
              practices; illegal acts; at the direction of a regulatory
              authority; or for any other violation of these Terms of Service.
              Utiva may also cancel the Subscription Plan if the Client or any
              of its Authorised Users is no longer a Utiva account holder in
              good standing.
            </p>
            <p>
              <strong>Changes to Subscription Plan</strong>
              Utiva reserves the right to make changes to the terms hereof
              and/or the Subscription Plan, in any manner and at any time as
              Utiva may determine in our sole and absolute discretion. Utiva
              will provide Client written notification of any material changes
              to the Subscription Plan, including any adjustments to the
              Subscription Plan for the Client’s Subscription Plan, at least 10
              days in advance of the implementation of such changes. After
              notice of a material change, Client may terminate the Client’s
              Subscription Plan by providing written notice to Utiva within the
              10-day notice period prior to the effective date of the change.
              Except as otherwise expressly provided for herein, any price
              changes or changes to the Client’s Subscription Plan will take
              effect following such 10-day notice period.
            </p>
            <p>
              <strong>Indemnity</strong>
              Client agrees to indemnify Utiva and its affiliates, and hold
              Utiva and its affiliates harmless from any damages, losses and
              costs related to third party (including any Authorised Users)
              claims or proceedings arising out of Client’s use and/or its
              Authorised Users’ use of the Utiva Platform, including without
              limitation infringements of rights, which are based on an unlawful
              use of the Utiva Engage Services by the Client and/or its
              Authorised Users or are made with the Client and/or its Authorised
              Users approval. The Client is obligated to notify Utiva
              immediately in cases where infringement is, or could be, imminent.
            </p>
            <p>
              <strong>Applicability</strong>
              It is hereby agreed that in the case of any contradiction and/or
              lack of clarity and/or ambiguity and/or incompatibility between
              the provisions of the Terms of the Service and this Section 12,
              the provisions specified in this Section 12 shall prevail in
              relation to the Services and the Utiva Platform.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Communications from Utiva
            </h3>
            <p>
              By creating an Account on our Platform you agree that we may
              contact you in relation to the Services. By creating an Account
              you also agree to subscribe to newsletters and/or marketing
              materials and/or other promotional information we may send to you.
              However, you may opt-out of receiving any, or all, of these
              marketing communications from us by following the unsubscribe link
              or instructions provided in any email we send. Please note that we
              may still contact you and send you messages related to the
              Services even after you have opted out of receiving marketing
              communications.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Electronic Signatures and Legal Notices
            </h3>
            <p>
              You agree that any signature or other electronic symbol or process
              attached to, or associated with a Contract, Form, certificate, or
              other document between you and Utiva or you and another User with
              the intent to sign, authenticate or accept the terms of any such
              Contract, Form, certificate, or other document and any contract
              formation or record-keeping through electronic means on the
              Services will have the same legal validity and enforceability as a
              manually executed signature or use of a paper-based recordkeeping
              system to the fullest extent permitted by applicable law,
              including the Federal Electronic Signatures in Global and National
              Commerce Act, or any similar state law based on the Uniform
              Electronic Transactions Act, and you hereby waive any objection to
              the contrary.
              <br /> <br />
              You consent to us providing notices to you under these Terms of
              Service electronically and understand that this consent has the
              same legal effect as a physical signature.
              <br /> <br />
              We may provide notices regarding activity and alerts
              electronically through your Account, email, and via text or SMS to
              the contact information provided to us by you. We will send
              notices affecting payment and these Terms through your Account or
              via email and you agree that they will be considered received 24
              hours after they are sent. You understand that you may not use the
              Services unless you consent to receive notices electronically. You
              may only withdraw consent to receive notices electronically by
              closing your Account.
              <br /> <br />
              We may send notices to Users’ mobile phones through text or SMS to
              the phone numbers you provide to us. These notices may include
              alerts about the Services, Contracts, Forms, Invoices and other
              documents. Administrators and Users may elect to not receive
              certain notices through via text or SMS, but this will limit the
              use of certain Services.
              <br /> <br />
              You must maintain an updated web browser and computer and mobile
              device operating systems to receive Notices correctly. You are
              responsible for all costs imposed by Internet or mobile service
              providers for sending or receiving notices electronically.
              <br /> <br />
              Contact us immediately via email to info@utiva.co if you are or
              believe you are having problems receiving Notices.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Interruption of Service
            </h3>
            <p>
              From time to time, the Services may be unavailable for periods of
              time for maintenance and / or modifications to the Platform. We
              will endeavor to make keep maintenance down time as brief as
              possible. However, we cannot guarantee that the Services will be
              available to you, and we will not be liable to you for any losses
              or damages, pecuniary or non-pecuniary, resulting from the
              interruption of your use of the Services.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Intellectual Property
            </h3>
            <p>
              The Platform, Services, any Content thereon (excluding any User
              Content), and any APIs, interfaces, features and functionalities
              thereof are and will remain the exclusive property of Utiva and
              its licensors. The Services are protected by copyright, trademark,
              and other laws of both the United States and each jurisdiction in
              which we make the Services available to Users. You may not use our
              trademarks, trade dress, service marks, logo or trade name in
              connection with any product or service without the prior written
              consent of Utiva. You will not remove, alter or conceal any
              copyright, trademark, service mark or other proprietary rights
              notices incorporated in the Platform and/or Services, if any.
              Except as expressly permitted herein, you may not copy, further
              develop, reproduce, republish, modify, alter download, post,
              broadcast, transmit or otherwise use the Content of the Platform
              or Services for any purpose.
              <br /> <br />
              You, or the Business, as applicable, have all right, title and
              interest in the User Content you submit. Except as otherwise
              agreed in any Consultant Services Agreement, by submitting any
              User Content, you grant Utiva and its successors and assignees a
              worldwide, non-exclusive, royalty-free, perpetual, sub-licensable
              and transferable license under any of your intellectual property,
              moral or privacy rights to use, copy, distribute, transmit,
              modify, prepare derivative works of, publicly display, alter,
              decompile, publicly perform such User Content on, through or in
              connection with the Platform and/or Services in any media formats
              and through any media channels. Except as expressly set forth
              herein, nothing herein grants Utiva any right, title or interest
              in any intellectual property rights of Client. Utiva shall not use
              any name or logo of Client in any marketing or advertising
              materials without Client’s prior written consent.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Feedback
            </h3>
            <p>
              We welcome feedback, comments and suggestions for improvements to
              the Services (“Feedback”). You acknowledge and expressly agree
              that any contribution of Feedback by you does not and will not
              give or grant you any right, title or interest in the Services or
              in any such Feedback. All Feedback will become the sole and
              exclusive property of Utiva, and Utiva may use and disclose
              Feedback in any manner and for any purpose whatsoever without
              further notice or compensation to you and without retention by you
              of any proprietary rights, or other right or claim. You hereby
              assign to Utiva any and all right, title and interest to any
              patent, copyright, trade secret, trademark, show-how, know-how,
              moral rights and any and all other intellectual property rights
              that you may have in and to any and all Feedback.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Links to Other Web Sites or Services
            </h3>
            <p>
              Our Services may contain links to third-party websites or services
              that are not owned or controlled by Utiva. Our Services may also
              allow you to import or interface with third-party applications or
              services.
              <br /> <br />
              Utiva has no control over, and assumes no responsibility for the
              content, privacy policies, or practices of any third-party
              websites or services. We do not warrant or guarantee that the
              offerings of any of these third-party, their services, or their
              websites.
              <br /> <br />
              You acknowledge and agree that Utiva will not be responsible or
              liable, directly or indirectly, for any damage or loss caused or
              alleged to be caused by or in connection with use of or reliance
              on any such content, goods or services available on or through any
              such third party web sites or services.
              <br /> <br />
              We strongly advise you to read the terms of service and privacy
              policies of any third-party web sites or services that you visit
              or interact with.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Termination; Survival
            </h3>
            <p>
              We may terminate or suspend your Account and bar access to the
              Services immediately, without prior notice or liability to you, in
              our sole discretion, for any reason whatsoever. Grounds for such
              termination or suspension may include: (i) extended periods of
              inactivity; (ii) breach of these Terms; (iii) fraudulent,
              harassing, or abusive behavior; (iv) behavior that is illegal or
              harmful to other Users, third parties or business interests of
              Utiva; (iv) termination, suspension or expiration of any
              Consultant Services Agreement for any reason, as applicable, or
              (v) failure to make payment in accordance with the terms hereof or
              any Consultant Services Agreement. If your Account is terminated,
              you may not rejoin the Platform again without our express
              permission. Upon termination of your Account, you shall not have
              any further access to any Content that may be available through
              your Account.
              <br /> <br />
              We reserve the right to investigate suspected violations of these
              Terms or illegal and inappropriate behavior through the Services.
              We will fully cooperate with any law enforcement authorities or
              court order requesting or directing us to disclose the identity,
              behavior or Content of anyone believed to have violated these
              Terms or to have engaged in illegal behavior in connection with
              the Services.
              <br /> <br />
              You or the Business, as applicable, may request the termination of
              your Account at any time by contacting us via email at
              info@utiva.co . Following such request, Utiva shall close your
              Account as soon as reasonably practicable.
              <br /> <br />
              Any suspension, cancellation or termination of your account shall
              not affect your obligations under these Terms which by their
              nature are intended to survive such suspension, cancellation or
              termination, including, without limitation, Section 10.4
              [Appointment of Utiva as Payment Agent], , Section 10.6 [Ownership
              of Deliverables], Section 15 [Intellectual Property], Section 16
              [Feedback], Section 19 [Indemnity and Limitations of Liability],
              Section 21 [Disclaimers], Section 22[Limitation of Liability];
              Section 25 [Arbitration and Class Action Waiver]; and Section 26
              [Confidentiality].
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Limitation of Liability; Indemnification
            </h3>
            <p>
              Utiva is not an intermediary, advisor, agent or third party to
              Users with regard to any Contracts, and we take no responsibility
              for the quality or adequacy of any Deliverables or services
              performed, User disputes and or content posted to the Platform by
              Users.
              <br /> <br />
              Utiva assumes no liability for any acts or omissions of any
              Contractor, Contractor's failure to provide the services to the
              Client, or Client's failure to pay amounts owed to a Contractor;
              and the Contractor acknowledges that Utiva's obligation to pay the
              Contractor as the Contractor’s Payment Agent is subject to and
              conditional upon Utiva's actual receipt of payment from the
              Client.
              <br /> <br />
              You agree to defend, indemnify and hold harmless Utiva its
              Affiliates, Payment Service Providers and each of their respective
              employees, contractors, agents, officers and directors, from and
              against any and all claims, damages, obligations, losses,
              liabilities, costs or debt, and expenses (including but not
              limited to attorney's fees), resulting from or arising out of (a)
              your use and access of the Service, by you or any person using
              your account and password; (b) a breach of these Terms; (c) your
              breach of the terms of any Contract, or the terms of any Payment
              Service Provider agreement; or (d) any Contract, Form, data and
              Content posted by you to the Platform.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Force Majeure
            </h3>
            <p>
              You may not hold us liable for any interruption of the Services
              due to any act of God; blockage, disturbance or encumbrance of the
              telecommunications, transport or procurement networks for whatever
              reason; poor quality or interruptions of electrical current; virus
              or computer pirate attacks, insurrections or acts of a similar
              nature; state of war or embargo; total or partial strikes within
              or outside of the company; lock-out, social conflicts, sabotage or
              acts of vandalism; foul weather, epidemics, earthquakes,
              explosion, fires, storms, flooding, other natural disasters; water
              damage, incapacity to obtain raw materials or supplies; legal or
              regulatory modifications applicable to supplying the Services, and
              any other case beyond our voluntary control preventing the normal
              provision of the Services to you.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Disclaimers
            </h3>
            <p>
              Utiva is not a law firm, and is not permitted to engage in the
              practice of law. Utiva employees do not act as your attorney or
              otherwise provide legal advice to you. The Templates, Forms, and
              other sample documents available to you on Utiva are made
              available to you for informational purposes only and are not a
              substitute for the advice of an attorney, and may not be relied
              upon by you in any manner whatsoever with regard to the legality
              or sufficiency of such materials for your situation or needs.
              <br /> <br />
              Your use any portion of the Services does not create an
              attorney-client relationship with us. You understand and agree
              that you or your attorneys or advisors represent you in any legal
              matter you undertake related in any manner to any Template, Form
              or any other document you obtain through the Platform.
              Accordingly, while communications between you and Utiva are
              protected by our Privacy Policy, they are not protected by any
              attorney-client privilege or attorney work product doctrine. Utiva
              is prohibited from providing any kind of advice, explanation,
              opinion, or recommendation to you about possible legal rights,
              remedies, defenses, options, selection of forms or strategies.
              <br /> <br />
              The Platform provides Templates, Forms and other automated
              document generation tools for Users to prepare, create and execute
              Contracts with other Users, document work progress, and fill out
              and submit Forms to tax authorities. The information we provide is
              comprised of a compilation of frequently encountered legal and
              compliance issues generally applicable to engagements between
              Contractors and Clients, and is not intended to be comprehensive
              of matters specific to your circumstances.
              <br /> <br />
              At no time do we review your Contracts, Forms, or other documents
              or the information you input for legal sufficiency, draw legal
              conclusions, provide legal advice or apply the law to the facts of
              your particular situation or needs.
              <br /> <br />
              Utiva and the Services are not a substitute for the advice of an
              attorney. Although Utiva takes every reasonable effort to ensure
              that the Templates, Forms, and other information on the Platform
              are up-to-date and reflect our best understanding of compliance
              matters related to engagements with independent contractors, the
              information on the Platform is not legal advice and is not
              guaranteed to be correct, complete or up-to-date. Because the law
              changes rapidly, varies from jurisdiction to jurisdiction, and is
              also subject to varying interpretations by different courts and
              certain government and administrative bodies, Utiva cannot
              guarantee that all the information on the Platform is accurate,
              up-to-date, complete or sufficient for your specific legal or
              compliance needs.
              <br /> <br />
              The Platform contains links to other compliance resources. We
              provide these links to help you identify and locate other
              resources that may be of interest to you, and are not intended to
              state or imply that Utiva sponsors, is affiliated or associated
              with, guarantees, or is legally authorized to use any trade name,
              registered trademark, logo, legal or official seal, or copyrighted
              symbol that may be reflected in the links. Any third party
              information contained on the Platform or on sites we link to is
              from sources we believe to be reliable, but which we have not
              independently verified.
              <br /> <br />
              Utiva is not responsible for any loss, injury, claim, liability,
              or damages related to your use of any sites we link to or from
              errors or omissions in the content of the linked sites. Your use
              third-party links and information at your own risk. Any
              tax-related compliance information on the Platform is not intended
              by us to be used, and cannot be used, for the purpose of (i)
              avoiding penalties that may be imposed by any governmental taxing
              authority or agency, or (ii) promoting, marketing or recommending
              to another party any tax-related advice in such information.
              <br /> <br />
              Any suggestions in the information we provide on the Platform are
              general, and do not take into account an individual’s or entity’s
              specific tax circumstances or applicable governing tax law, which
              may vary from jurisdiction to jurisdiction is subject to change.
              Utiva makes no express or implied warranties or representations,
              and Utiva has no liability to you with respect to the information
              and data we provide to you on the Platform or in connection with
              the Services.
              <br /> <br />
              YOUR USE OF THE SERVICES, AND ANY, CONTENT OR INFORMATION OBTAINED
              THROUGH THE SERVICES IS AT YOUR OWN RISK. THE SERVICES AND CONTENT
              ANDS INFORMATION OBTAINED THROUGH THE PLATFORM ARE PROVIDED ON AN
              "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT ANY EXPRESS OR IMPLIED
              IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, NON-INFRINGEMENT OR COURSE OF PERFORMANCE OR ANY OTHER.
              NEITHER Utiva NOR ITS AFFILIATES MAKE ANY WARRANTY OR
              REPRESENTATION WITH RESPECT TO THE COMPLETENESS, SECURITY,
              RELIABILITY, QUALITY, ACCURACY OR AVAILABILITY OF THE SERVICES OR
              INFORMATION OBTAINED BY YOU THROUGH THE PLATFORM. THE FOREGOING
              DOES NOT AFFECT ANY WARRANTIES WHICH CANNOT BE EXCLUDED OR LIMITED
              UNDER APPLICABLE LAW.
              <br /> <br />
              Utiva AND, ITS AFFILIATES DO NOT WARRANT THAT (A) THE SERVICES OR
              ANY PAYMENT METHOD WILL BE UNINTERRUPTED, SECURE OR AVAILABLE AT
              ANY PARTICULAR TIME OR LOCATION; (B) ANY ERRORS OR DEFECTS WILL BE
              CORRECTED; (C) THE SERVICES ARE FREE OF VIRUSES OR OTHER HARMFUL
              COMPONENTS; OR (D) THE RESULTS OF USING THE SERVICES WILL MEET
              YOUR REQUIREMENTS.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Limitation of liability
            </h3>
            <p>
              We assume no responsibility for any error, omission, interruption,
              deletion, defect, delay in operation or transmission,
              communications line failure, theft or destruction or unauthorized
              access to, or alteration of, any Content or Services. We are not
              responsible for any problems or technical malfunction of any
              telephone network or lines, computer online systems or equipment,
              servers or providers, software, failure due to technical problems
              or traffic congestion on the Internet or on the Services. Under no
              circumstances shall we be responsible for any loss or damage,
              including personal injury or death and any injury or damage to any
              person’s mobile device or computer, resulting from use of the
              Services or from any Content. In addition, we assume no
              responsibility for any incorrect data, including Personal Data
              provided by you or on your behalf and you hereby represent and
              warrant that you are solely responsible for any and all data
              provided to Utiva, including any incorrect data and you shall
              assume any and all liability for any consequences of provision of
              such incorrect data to us.
              <br /> <br />
              IN NO EVENT WILL Utiva, NOR ITS DIRECTORS, EMPLOYEES, PARTNERS,
              AGENTS, SUPPLIERS, OR AFFILIATES, BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, INCLUDING
              WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER
              INTANGIBLE LOSSES, RESULTING FROM (A) YOUR ACCESS TO OR USE OF OR
              INABILITY TO ACCESS OR USE THE SERVICES; (B) ANY CONDUCT OR
              CONTENT OF ANY USER OF THIRD PARTY ON THE SERVICES; (C) ANY
              CONTENT OBTAINED FROM THE SERVICE; AND (D) UNAUTHORIZED ACCESS,
              USE OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT, WHETHER BASED
              ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE) OR ANY OTHER
              LEGAL THEORY, WHETHER OR NOT WE HAVE BEEN INFORMED OF THE
              POSSIBILITY OF SUCH DAMAGE, AND EVEN IF A REMEDY SET FORTH HEREIN
              IS FOUND TO HAVE FAILED OF ITS ESSENTIAL PURPOSE.
              <br /> <br />
              OUR MAXIMUM LIABILITY TO YOU UNDER THESE TERMS IS LIMITED TO THE
              GREATER OF THE TOTAL AMOUNT OF FEES ACTUALLY PAID BY YOU TO US IN
              THE THREE MONTHS PRECEDING THE EVENT THAT IS THE BASIS OF YOUR
              CLAIM OR $500.00. THESE LIMITATIONS APPLY REGARDLESS OF THE LEGAL
              THEORY ON WHICH YOUR CLAIM IS BASED.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Exclusions
            </h3>
            <p>
              Some jurisdictions do not allow the exclusion of certain
              warranties or the exclusion or limitation of liability for
              consequential or incidental damages set forth in the “LIMITATION
              OF LIABILITY” and “DISCLAIMER” sections above, so the limitations
              above may not apply to you.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Governing Law
            </h3>
            <p>
              These Terms will be governed and construed in accordance with the
              laws of California, United States, without regard to its conflict
              of law provisions or by the JAMS Rules in the case or arbitrations
              as outlined below.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Arbitration and class action waiver
            </h3>
            <p>
              You agree to first attempt to resolve disputes with us in good
              faith and in a timely manner. Where no resolution can be found,
              you agree that any dispute, controversy, or claim (collectively,
              “Claim”) relating in any way to your use of the Services will be
              settled by final and binding arbitration in San Francisco,
              California, using the English language, before a single
              arbitrator. Claims involving amounts greater than $250,000 will
              apply the JAMS Comprehensive Arbitration Rules and Procedures; and
              Claims involving amounts less than or equal to $250,000 will apply
              the JAMS Streamlined Arbitration Rules then in effect (those rules
              are deemed to be incorporated by reference into this section, and
              as of the date of these Terms). Judgment on the arbitration award
              may be entered in any court that has jurisdiction. Any arbitration
              under these Terms will take place on an individual basis – class
              arbitrations and class actions are not permitted. You understand
              that by agreeing to these Terms, you and Utiva are each waiving
              the right to trial by jury or to participate in a class action or
              class arbitration. Notwithstanding the foregoing, you and Utiva
              will have the right to bring an action in a court of proper
              jurisdiction for injunctive or other equitable or conservatory
              relief, pending a final decision by the arbitrator. Payment for
              any and all reasonable JAMS filing, administrative and arbitrator
              fees will be in accordance with the JAMS Rules.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Confidentiality
            </h3>
            <p>
              “Confidential Information” means any business and technical
              information disclosed by one party (“Disclosing Party”) to the
              other party (“Receiving Party”) that is identified as confidential
              or proprietary, or which should reasonably be understood to be
              confidential or proprietary. Confidential Information does not
              include information which Receiving Party can show (a) is or has
              become publicly available without its breach of this Agreement;
              (b) was in its possession prior to disclosure, (c) was provided by
              a third party having a lawful right to make the disclosure; or (d)
              is required to be disclosed by law or a court order (“Order”),
              provided the Receiving Party provides prompt written notice of the
              requirement and cooperates with Disclosing Party as reasonably
              necessary to limit or eliminate such requirement, if and to the
              extent permitted by such Order. Receiving Party will not use the
              Confidential Information of the Disclosing Party except for
              performance of its obligations under this Agreement. The terms and
              conditions of this Agreement, including without limitation any
              pricing terms hereof, shall be deemed the Confidential Information
              of Utiva. All User Content added by a User to the Platform shall
              be deemed Confidential Information of the User, provided however,
              that Utiva may use any data received from the User (including but
              not limited to User Content) for its own internal purposes, such
              as, without limitation, the general improvement of its products
              and services or in order to recommend its services to third
              parties.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Miscellaneous
            </h3>
            <p>
              Except as may be expressly stated in these Terms, these Terms
              constitute the entire agreement between us and you pertaining to
              the subject matter hereof, and any and all other agreements
              existing between us relating thereto are hereby canceled. We
              reserve the right, at our sole discretion, to modify or replace
              these Terms at any time with notice to you. By continuing to
              access or use the Services after any revisions become effective,
              you agree to be bound by the revised Terms. If you do not agree to
              the new Terms, you are no longer authorized to use the Service. In
              the event that any provision of these Terms is held to be
              unenforceable, such provision shall be replaced with an
              enforceable provision which most closely achieves the effect of
              the original provision, and the remaining terms of these Terms
              shall remain in full force and effect. We may assign and/or
              transfer our rights and obligations hereunder to any third party
              without prior notice. You shall not assign and/or transfer any of
              your rights or obligations hereunder, and any assignment in
              violation of the foregoing shall be void. Either party’s failure
              to enforce any right or provision of these Terms will not be
              considered a waiver of those rights. If any provision of these
              Terms is held to be invalid or unenforceable by a court, the
              remaining provisions of these Terms will remain in effect. If we
              are required to provide notice to you hereunder, we may provide
              such notice to the contact details you provided upon registration.
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Contact Us
            </h3>
            <p>
              Our Customer Support team is available 24/7 if you have any
              questions regarding the Services, your Account, or these or Terms
              of Service. You can contact our Customer Support team by
              submitting a request here. Utiva may communicate with you via
              email or other channels regarding your Account, Platform system
              updates, and other issues related to your Account.
              <br /> <br />
              You may contact us regarding the Services or these Terms by US
              mail at:
              <br /> <br />
              3400 Prospect St NWWashington, DC 20007- +1-202-369-3961
            </p>
          </div>
          <div className="mb-8">
            <h3 className="text-default font-mono text-xl lg:text-2xl mb-4 font-semibold">
              Prohibited Activity List
            </h3>
            <p>
              <strong>Prohibited User Activities:</strong>
              <ul className="list-inside list-disc">
                <li>
                  Activities that relate to transactions that are associated
                  with purchases of annuities or lottery contracts, lay-away
                  systems, off-shore banking or transactions to finance or
                  refinance debts funded by a credit card
                </li>
                <li>
                  Activities that relate to transactions that are associated
                  with the sale of traveler's checks or money orders
                </li>
                <li>
                  Activities that relate to transactions that are by payment
                  processors to collect payments on behalf of merchants
                </li>
                <li>
                  Activities that relate to transactions that are for the sale
                  of certain items before the seller has control or possession
                  of the item
                </li>
                <li>
                  Activities that relate to transactions that involve certain
                  credit repair, debt settlement services, credit transactions
                  or insurance activities
                </li>
                <li>
                  Activities that relate to transactions that involve currency
                  exchanges or check cashing businesses
                </li>
                <li>
                  Activities that relate to transactions that involve offering
                  or receiving payments for the purpose of bribery or corruption
                </li>
                <li>
                  Activities that relate to transactions that show the personal
                  information of third parties in violation of applicable law
                </li>
                <li>
                  Activities that relate to transactions that support pyramid or
                  ponzi schemes, matrix programs, other "get rich quick" schemes
                  or certain multi-level marketing programs
                </li>
                <li>
                  Activities violating any law, statute, ordinance or regulation
                </li>
                <li>Firearms</li>
                <li>
                  Transactions involving ammunition, firearms, or certain
                  firearm parts or accessories
                </li>
                <li>
                  Transactions involving auctions including online auctions and
                  motor vehicles
                </li>
                <li>Transactions involving bearer share companies</li>
                <li>
                  Transactions involving certain sexually oriented materials or
                  services such as pornographic solicitation or material
                  including telephone, pictures, books, movies, t-shirts,
                  internet adult sites, etc.
                </li>
                <li>Transactions involving charities</li>
                <li>Transactions involving cigarettes</li>
                <li>
                  Transactions involving consulates, diplomatic missions,
                  economic citizenship or citizenship by investment programs
                </li>
                <li>
                  Transactions involving crowdfunding, penny stocks or microcap
                  securities
                </li>
                <li>Transactions involving drug paraphernalia</li>
                <li>
                  Transactions involving items that are considered obscene
                </li>
                <li>
                  Transactions involving items that encourage, promote,
                  facilitate or instruct others to engage in illegal activity
                </li>
                <li>
                  Transactions involving items that infringe or violate any
                  copyright, trademark, right of publicity or privacy or any
                  other proprietary right under the laws of any jurisdiction
                </li>
                <li>
                  Transactions involving narcotics, steroids, cannabis, certain
                  controlled substances or other products that present a risk to
                  consumer safety
                </li>
                <li>
                  Transactions involving precious metals dealers that purchase
                  metals from pawnbrokers and other secondary sources
                </li>
                <li>
                  Transactions involving scholarship programs, universities and
                  students for online tuition solutions
                </li>
                <li>
                  Transactions involving shell banks, as defined in Section
                  1010.605 of Title 31 of the U.S. Code of Federal Regulations
                </li>
                <li>
                  Transactions involving shipping and general trading companies
                  operating in free trade zones
                </li>
                <li>
                  Transactions involving stolen goods including digital and
                  virtual goods
                </li>
                <li>
                  Transactions involving the promotion of hate, violence, racial
                  or other forms of intolerance that is discriminatory or the
                  financial exploitation of a crime
                </li>
                <li>
                  Transactions involving weapons or knives regulated under
                  applicable law
                </li>
                <li>
                  Transactions that involve the sales of products or services
                  identified by government agencies to have a high likelihood of
                  being fraudulent
                </li>
              </ul>
            </p>
            <p>
              <strong>Prohibited Uses:</strong>
              <ul className="list-inside list-disc">
                <li>
                  Duplicate, decompile, reverse engineer, disassemble or decode
                  the Services (including any underlying idea or algorithm), or
                  attempt to do any of the same;
                </li>
                <li>
                  Access or use the Services in any manner that could disable,
                  overburden, damage, disrupt or impair the Services or
                  interfere with any other party's access to or use of the
                  Services or use any device, software or routine that causes
                  the same;
                </li>
                <li>
                  Attempt to gain unauthorized access to, interfere with, damage
                  or disrupt the Services, Accounts registered to other Users,
                  or the computer systems or networks connected to the Services;
                </li>
                <li>
                  Circumvent, remove, alter, deactivate, degrade or thwart any
                  technological measure or content protections of the Services;
                </li>
                <li>
                  Use any robot, spider, crawlers or other automatic device,
                  process, software or queries that intercepts, "mines," scrapes
                  or otherwise accesses the Services to monitor, extract, copy
                  or collect information or data from or through the Services,
                  or engage in any manual process to do the same;
                </li>
                <li>
                  Introduce any viruses, trojan horses, worms, logic bombs or
                  other materials that are malicious or technologically harmful;
                </li>
                <li>
                  Use the Services for illegal, harassing, unethical, or
                  disruptive purposes;
                </li>
                <li>
                  Violate any applicable law or regulation in connection with
                  your access to or use of the Services;
                </li>
                <li>
                  Use or access another User's account or password without
                  permission; or
                </li>
                <li>
                  Access or use the Services in any way not expressly permitted
                  by these Terms or any Consultant Services Agreement.
                </li>
              </ul>
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsAndConditions;
